import { isStartBusinessTypePathname } from 'router/params/startBusinessType';
import type { FullPathToStepName, RoutePaths } from 'router/types';

import type { FlagsContext } from 'providers/FlagsProvider';

import type { Steps } from './useSignupSteps';

type ExcludedFullPaths = '/offer/decline' | '/email-validation' | '/status';

type AvailableFullPaths = Exclude<RoutePaths, ExcludedFullPaths>;

type StepInfo<Paths extends RoutePaths = RoutePaths> = {
  name: FullPathToStepName<Paths>;
  isEnabled: boolean;
  version: 'v2';
  fullPath: Paths;
};

function buildStepsMap<T extends { isEnabled: boolean }>(
  items: T[],
): Record<number, T> {
  return items.reduce<Record<number, T>>((acc, item) => {
    if (item.isEnabled) {
      acc[Object.keys(acc).length + 1] = item;
    }
    return acc;
  }, {});
}

/**
 * Resolves the available steps in the signup process, including any experimental steps.
 *
 * @param flags - An optional record of feature flags that determine which steps are enabled.
 * @returns A record of steps with their respective information, including any experimental steps.
 */
export function resolveGymSteps(
  _flags?: FlagsContext['flags'],
  isBypassActivities = false,
) {
  return buildStepsMap([
    {
      name: 'welcome',
      isEnabled: true,
      version: 'v2',
      fullPath: '/',
    },
    {
      name: 'gym-business-details',
      isEnabled: true,
      version: 'v2',
      fullPath: '/gym/business-details',
    },
    {
      name: 'activities',
      isEnabled: !isBypassActivities,
      version: 'v2',
      fullPath: '/activities',
    },
    {
      name: 'pricing',
      isEnabled: true,
      version: 'v2',
      fullPath: '/pricing',
    },
    {
      name: 'gym-tax-details',
      isEnabled: true,
      version: 'v2',
      fullPath: '/gym/tax-details',
    },
    {
      name: 'offer',
      isEnabled: true,
      version: 'v2',
      fullPath: '/offer',
    },
    {
      name: 'success',
      isEnabled: true,
      version: 'v2',
      fullPath: '/success',
    },
  ] satisfies StepInfo<AvailableFullPaths>[]);
}

/**
 * Creates a route mapping object with widened string index signature while ensuring
 * type safety for known route paths at compile time.
 *
 * This helper function allows you to define route mappings that are validated against
 * your route types during development, but can be accessed with any string key at runtime.
 * This is useful when you need to handle dynamic route lookups or when working with
 * route paths that might not be known at compile time.
 *
 * @template T - The specific mapping type that must satisfy the route path constraints
 * @param {T} map - An object mapping route paths to their corresponding step paths.
 *                  Must satisfy Partial<Record<RoutePaths, Steps[number]['fullPath']>>
 * @returns {Record<string, string | undefined>} A widened record that accepts any string key
 *
 * @example
 * ```typescript
 * const routeMap = createRouteMap({
 *   '/offer/decline': '/offer',
 *   '/profile/edit': '/profile'
 * });
 *
 * // TypeScript validates these at compile time
 * routeMap['/offer/decline'] // ✓ valid
 *
 * // But also allows arbitrary string access at runtime
 * const dynamicPath = getUserPath(); // returns any string
 * routeMap[dynamicPath] // ✓ also valid, returns string | undefined
 * ```
 */
function createRouteMap<
  T extends Partial<Record<RoutePaths, Steps[number]['fullPath']>>,
>(map: T): Record<string, string | undefined> {
  return map;
}

const noStepMap = createRouteMap({ '/offer/decline': '/offer' });

/**
 * The step every `/start/*` landing route belongs to: the same conceptual step
 * as `/`, since both are where a lead is first created.
 *
 * `StepInfo` holds a single `fullPath` per step, so these paths can't be listed
 * in `resolveGymSteps` alongside `/`. They are matched
 * explicitly here — deliberately not left to the fallback below, which returns
 * the same value today but carries no such intent — see
 * docs/adr/013-explicit-step-1-registration-for-start-routes.md
 */
const START_ROUTES_STEP_INDEX = 1;

/**
 * Finds the index of a step in the steps object that matches the given path.
 *
 * @param steps - The steps object containing step definitions with their full paths
 * @param path - The path to search for in the steps
 * @returns The numeric index of the matching step, or 1 if no match is found
 */
export function findStepIndex(steps: Steps, path: string) {
  const effectivePath = noStepMap[path] ?? path;

  const entry = Object.entries(steps).find(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ([_, value]) => value.fullPath === effectivePath,
  );

  if (entry) return Number(entry[0]);

  if (
    isStartBusinessTypePathname(effectivePath) ||
    effectivePath === '/start/others'
  ) {
    return START_ROUTES_STEP_INDEX;
  }

  return 1;
}

/**
 * The path a jump to `step` lands on: `/success` for the last step, and the
 * step's own path for any other. `undefined` when `step` is out of range.
 */
export function findStepPath(steps: Steps, step: number) {
  const totalSteps = Object.keys(steps).length;

  if (step < 1 || step > totalSteps) return undefined;

  return step === totalSteps ? '/success' : steps[step]?.fullPath;
}

/**
 * Finds the index of a step by its name within the provided steps array.
 *
 * @param steps - An array of step objects, each containing a `name` property.
 * @param name - The name of the step to find.
 * @returns The index of the step as a number if found; otherwise, returns 1.
 */
function findStepIndexByName(steps: Steps, name: Steps[number]['name']) {
  const entry = Object.entries<Steps[number]>(steps).find(
    ([_, value]) => value.name === name,
  );

  return entry ? Number(entry[0]) : 1;
}

/**
 * Finds the best next step in a sequence of steps based on the current step's name.
 * If the current step is invalid (not found), it searches backwards for the nearest valid step,
 * then returns the step immediately following it.
 *
 * @param steps - An array of step objects.
 * @param name - The name of the current step to find the next step from.
 * @returns The next step object after the current or nearest valid step, or undefined if there is none.
 */
export function findBestNextStep(
  steps: Steps,
  name: Steps[number]['name'] = 'welcome',
) {
  const lastAvailableStepIndex = Object.keys(steps).length;
  const currentIndex = findStepIndexByName(steps, name);

  const beforeLastIndex = lastAvailableStepIndex - 1;

  if (currentIndex > beforeLastIndex) {
    return beforeLastIndex;
  }

  if (currentIndex > 1 && currentIndex < beforeLastIndex) {
    return currentIndex + 1;
  }

  let validIndex = currentIndex;

  while (steps[validIndex] === undefined && validIndex > 1) {
    validIndex -= 1;
  }

  const bestNextStep = steps[validIndex + 1];

  const nextStep = findStepIndexByName(steps, bestNextStep?.name ?? 'success');

  return nextStep;
}
